import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseFieldErrors, parseErrorMessage, parseErrorStatus } from "@/lib/queryClient";
import { computeGrossMonthly, computeDiscountedMonthly } from "@/lib/promo-preview";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { CurrentPlanCard as ModularCurrentPlanCard, type AccountPlanSummary } from "@/components/billing/current-plan-card";
import { GuaranteeStatusCard, type GuaranteeState } from "@/components/billing/guarantee-status-card";
import { V3PlanCard } from "@/components/billing/v3-plan-card";
import { useV3Billing } from "@/hooks/use-v3-billing";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import type { StripeConfig } from "@/components/host-payment-methods";
import { StripeSetupBannerSection } from "@/components/stripe-setup-banner";
import { PaginationControls, clampPage } from "@/components/pagination-controls";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  CreditCard,
  Loader2,
  Check,
  AlertTriangle,
  Receipt,
  TrendingUp,
  Zap,
  Building2,
  Search,
  Building,
  ArrowRight,
  RefreshCw,
  Wallet,
  ArrowUpCircle,
  ArrowDownCircle,
  Clock,
  X,
  Gift,
  Lock,
  Tag,
  CheckCircle2,
  Info,
} from "lucide-react";
import { ACCOUNT_TYPES, MONTHLY_PRICES_PER_ROOM, isFounderTier, isGrowthOrProfessionalTier, isPaidTier, isFlatPricedTier, flatPlanMonthlyPriceUsd, hasUnlimitedSearch, chargeableRoomsForPlan, hasFeatureAccess, planDisplayName, type AccountType } from "@shared/schema";
import { UpgradeModal } from "@/components/upgrade-modal";
import { format } from "date-fns";
import { usePageTitle } from "@/hooks/use-page-title";

type BillingSummary = {
  plan: AccountType;
  cycle: string;
  billedRooms: number;
  shadowRooms: number;
  chargeableRooms: number;
  pricePerRoom: number | null;
  monthlyTotal: number;
  annualTotal: number;
  subscriptionStatus: string;
  founderStatus: string | null;
  founderFreeRooms: number | null;
  founderOverageRooms: number | null;
  founderOverageRateMonthly: number | null;
  founderOverageMonthlyCharge: number | null;
  founderOverageSubscriptionId: string | null;
  founderOveragePaymentFailed: boolean | null;
  isFlatPricedPlan?: boolean;
  subscriptionPeriodStart: string | null;
  subscriptionPeriodEnd: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  paymentMethod: {
    brand: string | null;
    last4: string | null;
    expMonth: number | null;
    expYear: number | null;
    expiringSoon: boolean;
  } | null;
  pendingProrationRooms: number;
  pendingProrationChargeCents: number;
  pendingProrationBreakdown?: Array<{ propertyId: number | null; propertyName: string | null; rooms: number; amountCents: number }>;
  pendingProrationCreditCents: number;
  pendingProrationCreditEvents: number;
  pendingProrationCreditBreakdown?: Array<{ propertyId: number | null; propertyName: string | null; rooms: number; amountCents: number }>;
  nextSettlementAt: string | null;
  scheduledPlan: AccountType | null;
  scheduledCycle: string | null;
  scheduledChangeAt: string | null;
  scheduledChangeReminderSentAt: string | null;
  scheduledChangeReminderLeadDays: number;
  seamLockUsage?: {
    seamLockCount: number;
    unitPriceCents: number;
    totalCents: number;
    snapshotMonth: string;
  };
  activePromo?: {
    code: string;
    discountType: "percent" | "fixed";
    discountValue: string;
    duration: "forever" | "repeating";
    durationMonths: number | null;
    expiresAt: string | null;
    discountedMonthlyTotal: number | null;
    discountedAnnualTotal: number | null;
    monthlySavings: number | null;
  } | null;
};

const PLAN_FEATURES: Record<string, { limit: string; included: string[] }> = {
  Research: {
    limit: "1 user · No property management",
    included: [
      "Property research tools",
      "Market analysis",
      "Underwriting calculator",
      "Buy Box search — free: 1 scan/day (100 results), unlimited when subscribed",
    ],
  },
  Host: {
    limit: "1 user · 1 active property · up to 10 rooms",
    included: [
      "Property research tools",
      "Market analysis",
      "Underwriting calculator",
      "Unlimited Buy Box search",
      "1 active property (up to 10 rooms)",
      "Tenant messaging",
      "Maintenance tracking",
      "PadSplit sync",
    ],
  },
  Growth: {
    limit: "Up to 3 team members · Unlimited properties (starts at 2 properties / 11 rooms)",
    included: [
      "Property research tools",
      "Market analysis",
      "Underwriting calculator",
      "Unlimited Buy Box search",
      "Unlimited active property listings",
      "Tenant messaging",
      "Maintenance tracking",
      "Team collaboration (up to 3 seats)",
      "Image generator",
      "Pricing automation",
      "PadSplit sync",
    ],
  },
  Professional: {
    limit: "Up to 10 team members · Unlimited everything",
    included: [
      "Property research tools",
      "Market analysis",
      "Underwriting calculator",
      "Unlimited Buy Box search",
      "Unlimited active property listings",
      "Tenant messaging",
      "Maintenance tracking",
      "Team collaboration (up to 10 seats)",
      "Image generator",
      "Pricing automation",
      "Financials & device control",
      "PadSplit sync",
      "Priority support",
    ],
  },
  PMPro: {
    limit: "Custom team size · Unlimited everything",
    included: [
      "Everything in Professional",
      "Owner Portal & CRM",
      "Owner billing overrides",
      "PM fee settings",
      "Custom users",
      "Priority support",
    ],
  },
};

type ProrationEvent = {
  id: string;
  type: "proration";
  userId: number;
  roomsAdded: number;
  propertyId: number | null;
  propertyName: string | null;
  eventAt: string;
  processedAt: string | null;
  stripeInvoiceItemId: string | null;
  notes: string | null;
};

type SeamLockChargeEvent = {
  id: string;
  type: "seam_lock_charge";
  userId: number;
  snapshotMonth: string;
  seamLockCount: number;
  unitPriceCents: number;
  totalCents: number;
  stripeLineItemId: string | null;
  settledAt: string;
};

type BillingHistoryEvent = ProrationEvent | SeamLockChargeEvent;

const NON_SELECTABLE_PLANS: AccountType[] = ["Interest", "Enterprise", "PMPro"];
const SELECTABLE_PLANS = ACCOUNT_TYPES.filter(
  (p): p is AccountType => !NON_SELECTABLE_PLANS.includes(p as AccountType)
);

const PLAN_ICONS: Record<string, typeof Search> = {
  Research: Search,
  Host: Building2,
  Growth: TrendingUp,
  Professional: Zap,
  Enterprise: Building,
  PMPro: Building,
  Interest: Search,
};

const PLAN_COLORS: Record<string, string> = {
  Research: "bg-blue-50 border-blue-200 text-blue-700",
  Host: "bg-green-50 border-green-200 text-green-700",
  Growth: "bg-orange-50 border-orange-200 text-orange-700",
  Professional: "bg-gray-900 border-gray-700 text-white",
  Enterprise: "bg-purple-50 border-purple-200 text-purple-700",
  PMPro: "bg-purple-50 border-purple-200 text-purple-700",
  Interest: "bg-gray-50 border-gray-200 text-gray-600",
};

const STATUS_STYLES: Record<string, string> = {
  active: "bg-green-100 text-green-700",
  none: "bg-gray-100 text-gray-600",
  canceled: "bg-red-100 text-red-700",
  past_due: "bg-amber-100 text-amber-700",
  trialing: "bg-blue-100 text-blue-700",
};

function formatCurrency(amount: number) {
  return `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "—";
  return format(new Date(dateStr), "MMM d, yyyy");
}

export default function AppAccountBilling() {
  const queryClient = useQueryClient();
  usePageTitle("Billing");
  const { toast } = useToast();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [upgradeTarget, setUpgradeTarget] = useState<AccountType | null>(null);
  const [downgradeTarget, setDowngradeTarget] = useState<AccountType | null>(null);
  const [cycleTarget, setCycleTarget] = useState<"monthly" | "annual" | null>(null);
  const [planFieldErrors, setPlanFieldErrors] = useState<Record<string, string>>({});
  const [cycleFieldErrors, setCycleFieldErrors] = useState<Record<string, string>>({});
  const [pendingCombinedUpgrade, setPendingCombinedUpgrade] = useState(false);
  const [pendingPromoCode, setPendingPromoCode] = useState<string>("");
  const [pendingPromoInvalid, setPendingPromoInvalid] = useState(false);

  // Messaging numbers are BETA-gated (messaging_provisioning), not
  // tier-gated — the modular model has no Professional tier to pitch.
  // Hook must run unconditionally, before the loading/error early returns.
  const summaryQuery = useQuery<BillingSummary>({
    queryKey: ["/api/app/billing/summary"],
  });

  // Modular pricing: 200 = the account is on the modular model (render the
  // modular plan + guarantee cards, hide the legacy tier picker); 409/errors
  // = legacy pre-cutover account, keep the legacy UI.
  const modularPlanQuery = useQuery<AccountPlanSummary & { guarantee: GuaranteeState }>({
    queryKey: ["/api/app/account/plan"],
    retry: false,
  });
  // Shape-checked (not just truthy): legacy accounts get a 409 here, and
  // test stubs may answer unmatched routes with {} — only a real payload
  // (accountKind present) switches the page to the modular UI.
  const isModular =
    !modularPlanQuery.isError &&
    !!modularPlanQuery.data?.accountKind &&
    !!modularPlanQuery.data?.quote &&
    !!modularPlanQuery.data?.guarantee;

  const historyQuery = useQuery<BillingHistoryEvent[]>({
    queryKey: ["/api/app/billing/history"],
  });

  // v3 per-property accounts get the "Your plan" card; the legacy per-room
  // plan picker, module cards, promo field and portal-based card actions are
  // hidden (not removed) for them. Legacy accounts get { billingModel: null }.
  const v3 = useV3Billing();
  const isV3 = v3 !== null;

  type EarningsCredit = { id: number; creditType: string; amountCents: number; expiresAt: string; redeemedAt: string | null };
  type EarningsResponse = { activeCreditCents: number; credits: EarningsCredit[] };
  const creditsQuery = useQuery<EarningsResponse, Error, { activeCreditCents: number; credits: EarningsCredit[] }>({
    queryKey: ["/api/app/refer-host/earnings"],
    select: (data: EarningsResponse) => ({ activeCreditCents: data.activeCreditCents || 0, credits: (data.credits || []).filter((c: EarningsCredit) => !c.redeemedAt) }),
  });

  const PLAN_RANK: Partial<Record<AccountType, number>> = {
    Research: 0, Interest: 0, Host: 1, Growth: 2, Professional: 3, Enterprise: 4, PMPro: 5,
    Founder25: 6, Founder50: 7, Founder100: 8,
  };

  const changePlanMutation = useMutation({
    mutationFn: async ({ plan, scheduled }: { plan: AccountType; scheduled?: boolean }) => {
      const res = await apiRequest("POST", "/api/app/billing/change-plan", { plan, scheduled });
      return res.json();
    },
    onSuccess: (data, variables) => {
      setPlanFieldErrors({});
      queryClient.invalidateQueries({ queryKey: ["/api/app/billing/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/team-limit"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/auth/me"] });
      if (data.scheduled) {
        toast({ title: "Downgrade scheduled", description: "Your plan will change at the end of your current billing cycle." });
      } else {
        const prevRank = PLAN_RANK[summaryQuery.data?.plan as AccountType] ?? 0;
        const nextRank = PLAN_RANK[variables.plan] ?? 0;
        if (nextRank < prevRank) {
          toast({ title: "Plan changed", description: "Your plan has been updated." });
        } else {
          toast({ title: "Plan upgraded", description: "Your new plan is active and you've been charged a prorated amount." });
        }
      }
      setDowngradeTarget(null);
      // For combined upgrade+annual, keep the dialog open so the cycle mutation
      // can render its own inline errors if it fails.
      if (!pendingCombinedUpgrade) {
        setUpgradeTarget(null);
      }
    },
    onError: (err: Error) => {
      setPendingCombinedUpgrade(false);
      const next = parseFieldErrors(err);
      if (Object.keys(next).length > 0) {
        setPlanFieldErrors(next);
        toast({
          title: "Please fix the highlighted fields",
          description: Object.values(next).join(" "),
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Error",
        description: parseErrorMessage(err, "Failed to change plan."),
        variant: "destructive",
      });
    },
  });

  const changeCycleMutation = useMutation({
    mutationFn: async ({ cycle, scheduled }: { cycle: string; scheduled?: boolean }) => {
      const res = await apiRequest("POST", "/api/app/billing/change-cycle", { cycle, scheduled });
      return res.json();
    },
    onSuccess: (data, variables) => {
      setCycleFieldErrors({});
      queryClient.invalidateQueries({ queryKey: ["/api/app/billing/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/team-limit"] });
      if (data.scheduled) {
        const currentCycle = summaryQuery.data?.cycle ?? "annual";
        const targetCycle = currentCycle === "annual" ? "monthly" : "annual";
        const effectiveDateRaw =
          data.scheduledChangeAt ??
          summaryQuery.data?.scheduledChangeAt ??
          summaryQuery.data?.subscriptionPeriodEnd ??
          null;
        const effectiveDate = formatDate(effectiveDateRaw);
        toast({
          title: "Cycle change scheduled",
          description: `You'll stay on ${currentCycle} until ${effectiveDate}, then move to ${targetCycle} billing.`,
        });
      } else if (variables.cycle === "annual") {
        const renewalDateStr = data.renewalDate ? formatDate(data.renewalDate) : null;
        if (typeof data.chargedAmountCents === "number") {
          const chargedStr = formatCurrency(data.chargedAmountCents / 100);
          const desc = renewalDateStr
            ? `You've been charged ${chargedStr} today and your next renewal is on ${renewalDateStr}.`
            : `You've been charged ${chargedStr} today for the year.`;
          toast({ title: "Switched to annual billing", description: desc });
        } else {
          const desc = renewalDateStr
            ? `Your billing cycle has been updated to annual. Next renewal on ${renewalDateStr}.`
            : "Your billing cycle has been updated to annual.";
          toast({ title: "Billing cycle updated", description: desc });
        }
      } else {
        toast({ title: "Billing cycle updated", description: "You're now on monthly billing." });
      }
      setCycleTarget(null);
      if (pendingCombinedUpgrade) {
        setPendingCombinedUpgrade(false);
        setUpgradeTarget(null);
      }
    },
    onError: (err: Error) => {
      const wasCombined = pendingCombinedUpgrade;
      setPendingCombinedUpgrade(false);
      const next = parseFieldErrors(err);
      if (Object.keys(next).length > 0) {
        setCycleFieldErrors(next);
        toast({
          title: "Please fix the highlighted fields",
          description: Object.values(next).join(" "),
          variant: "destructive",
        });
        return;
      }
      // For non-field errors during a combined upgrade, close the upgrade
      // dialog since the plan change already succeeded.
      if (wasCombined) {
        setUpgradeTarget(null);
      }
      toast({
        title: "Error",
        description: parseErrorMessage(err, "Failed to update billing cycle."),
        variant: "destructive",
      });
    },
  });

  const cancelMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/billing/cancel", {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/billing/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/team-limit"] });
      toast({ title: "Subscription canceled", description: "Your subscription has been canceled." });
      setCancelOpen(false);
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: parseErrorMessage(err, "Failed to cancel subscription."),
        variant: "destructive",
      });
    },
  });

  const managePaymentMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/billing/manage-payment", {});
      return res.json();
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast({ title: "Coming soon", description: data.message || "Payment management will be available once billing is activated." });
      }
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: parseErrorMessage(err, "Failed to open payment management."),
        variant: "destructive",
      });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/app/billing/reactivate", {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/billing/summary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/auth/me"] });
      toast({ title: "Subscription reactivated", description: "Your subscription will continue at the next renewal." });
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: parseErrorMessage(err, "Failed to reactivate subscription."),
        variant: "destructive",
      });
    },
  });

  const handleManagePayment = (currentSummary: BillingSummary) => {
    // Comped/manually-activated users have subscriptionStatus 'active' but no
    // Stripe customer, so the billing portal can't open for most plan types.
    // Route Growth/Professional users through Checkout to create the Stripe
    // customer + subscription in one flow. Founder users go through
    // managePaymentMutation directly — the backend lazily creates a Stripe
    // customer and opens the Billing Portal so they can add/replace a card
    // (needed for overage billing). Enterprise has custom pricing and falls
    // through to the "Contact support" path below.
    if (!currentSummary.stripeCustomerId) {
      if (isGrowthOrProfessionalTier(currentSummary.plan)) {
        const cycle = currentSummary.cycle === "annual" ? "annual" : "monthly";
        checkoutMutation.mutate({ plan: currentSummary.plan, cycle, promoCode: pendingPromoCode && !pendingPromoInvalid ? pendingPromoCode : undefined });
        return;
      }
      if (isFounderTier(currentSummary.plan)) {
        managePaymentMutation.mutate();
        return;
      }
      toast({
        title: "Contact support to add a payment method",
        description: "Your plan needs to be set up by our team before a card can be added.",
      });
      return;
    }
    managePaymentMutation.mutate();
  };

  const checkoutMutation = useMutation({
    mutationFn: async ({ plan, cycle, promoCode }: { plan: AccountType; cycle: "monthly" | "annual"; promoCode?: string }) => {
      const body: Record<string, string> = { plan, cycle };
      if (promoCode) body.promoCode = promoCode;
      const res = await apiRequest("POST", "/api/app/billing/checkout-session", body);
      return res.json();
    },
    onSuccess: (data) => {
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast({
          title: "Checkout unavailable",
          description: data?.message || "Could not start checkout. Please try again.",
          variant: "destructive",
        });
      }
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: parseErrorMessage(err, "Failed to start checkout."),
        variant: "destructive",
      });
    },
  });

  // Conference signup deep-link: /app/account/billing?startCheckout=growth&promo=CONF2026
  // Pre-fills the promo and routes straight into Stripe Checkout for new (free)
  // accounts. Fires once; the query string is cleared so a refresh won't repeat it.
  const autoCheckoutFired = useRef(false);
  useEffect(() => {
    if (autoCheckoutFired.current) return;
    const summaryData = summaryQuery.data;
    if (!summaryData) return;
    const params = new URLSearchParams(window.location.search);
    const promo = params.get("promo");
    const startCheckout = params.get("startCheckout");
    if (!promo && !startCheckout) return;
    autoCheckoutFired.current = true;
    const promoCode = promo ? promo.trim().toUpperCase() : undefined;
    if (promoCode) setPendingPromoCode(promoCode);
    window.history.replaceState({}, "", window.location.pathname);
    if (startCheckout) {
      const planMap: Record<string, AccountType> = { growth: "Growth", professional: "Professional" };
      const targetPlan = planMap[startCheckout.toLowerCase()] ?? "Growth";
      if (!summaryData.stripeSubscriptionId) {
        const cycle = summaryData.cycle === "annual" ? "annual" : "monthly";
        checkoutMutation.mutate({ plan: targetPlan, cycle, promoCode });
      } else {
        setUpgradeTarget(targetPlan);
      }
    }
  }, [summaryQuery.data]);

  if (summaryQuery.isLoading) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AppLayout>
    );
  }

  if (summaryQuery.isError) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="py-12 text-center">
            <CreditCard className="mx-auto h-10 w-10 text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">Billing is only available to account owners and team admins.</p>
            <p className="text-sm text-gray-400 mt-1">Contact your account owner or a team admin to manage billing settings.</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!summaryQuery.data) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="py-12 text-center text-gray-400">Failed to load billing information.</div>
        </div>
      </AppLayout>
    );
  }

  const summary = summaryQuery.data;
  const history = historyQuery.data || [];
  const rawPlanPrice = MONTHLY_PRICES_PER_ROOM[summary.plan];
  // Flat-priced entry tiers (Research/Host) have a $0 per-room rate but are not
  // "free" once subscribed — they bill a flat monthly price. Only treat them as
  // free when there's no active paid subscription (free Research / lapsed).
  const isFlatPlanTier = isFlatPricedTier(summary.plan);
  const hasPaidFlatSub = isFlatPlanTier && hasUnlimitedSearch({ plan: summary.plan, subscriptionStatus: summary.subscriptionStatus });
  const isFreePlan = isFlatPlanTier ? !hasPaidFlatSub : rawPlanPrice === 0;
  const isEnterprisePlan = rawPlanPrice === null && (summary.plan === "Enterprise" || summary.plan === "PMPro");
  const statusLabel = summary.subscriptionStatus.replace(/_/g, " ");
  const statusStyle = STATUS_STYLES[summary.subscriptionStatus] || STATUS_STYLES.none;
  // Founders and comped accounts have free, team-managed access (no Stripe
  // subscription drives their plan), so the self-serve plan picker, promo
  // field, cancel link, and cycle toggle don't apply — they get a read-only
  // explanation of their comped status instead (Task #3818).
  const isFounder = summary.founderStatus === "active";
  const isComped = summary.subscriptionStatus === "comped";
  const isCompedOrFounder = isFounder || isComped;

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="text-billing-title" data-tour="billing-heading">Billing</h1>
            <p className="text-gray-500 mt-1">Manage your plan, payment method, and billing history.</p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="billing" />
            <GuideButton pageKey="billing" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="billing" />

        <StripeSetupBannerSection />

        {v3 ? (
          <V3PlanCard status={v3} />
        ) : isModular && modularPlanQuery.data ? (
          <>
            <ModularCurrentPlanCard plan={modularPlanQuery.data} />
            <GuaranteeStatusCard
              guarantee={modularPlanQuery.data.guarantee}
              trialOver={
                !!modularPlanQuery.data.trial?.endsAt &&
                !modularPlanQuery.data.trial.active &&
                !["active", "trialing"].includes(modularPlanQuery.data.subscriptionStatus)
              }
            />
          </>
        ) : (
          <CurrentPlanCard
            summary={summary}
            isFreePlan={isFreePlan}
            isEnterprisePlan={isEnterprisePlan}
            statusStyle={statusStyle}
            statusLabel={statusLabel}
            onToggleCycle={() => setCycleTarget(summary.cycle === "annual" ? "monthly" : "annual")}
            isCycleChanging={changeCycleMutation.isPending}
            hideCycleToggle={isCompedOrFounder}
          />
        )}

        {isFounder && !isV3 && (
          <FounderOverageBillingPanel summary={summary} />
        )}

        {!isFounder && isComped && !isV3 && (
          <Card className="p-5 border-emerald-200 bg-emerald-50" data-testid="card-comped-explanation">
            <div className="flex items-start gap-3">
              <Gift className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-emerald-900 text-sm" data-testid="text-comped-title">
                  Your plan is comped
                </p>
                <p className="text-sm text-emerald-800 mt-1" data-testid="text-comped-description">
                  Your account has been comped with full {planDisplayName(summary.plan)} access at no charge. There&apos;s no subscription to manage. To change your plan, reach out to our team.
                </p>
              </div>
            </div>
          </Card>
        )}

        {!isCompedOrFounder && !isV3 && (
          <PromoCodeSection
            subscriptionId={summary.stripeSubscriptionId}
            subscriptionStatus={summary.subscriptionStatus}
            summaryActivePromo={summary.activePromo ?? null}
            summary={summary}
            pendingCode={pendingPromoCode}
            onPendingCodeChange={setPendingPromoCode}
            onPendingCodeValidityChange={setPendingPromoInvalid}
          />
        )}

        {!isV3 && summary.paymentMethod?.expiringSoon && summary.subscriptionStatus !== "past_due" && (
          <Card className="p-4 border-yellow-300 bg-yellow-50" data-testid="banner-card-expiring">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-700 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-yellow-900 text-sm">Your card is about to expire</p>
                <p className="text-xs text-yellow-800 mt-0.5" data-testid="text-card-expiring-detail">
                  {(summary.paymentMethod.brand?.toUpperCase() || "Card")}
                  {summary.paymentMethod.last4 ? ` ending in ${summary.paymentMethod.last4}` : ""}
                  {summary.paymentMethod.expMonth && summary.paymentMethod.expYear
                    ? ` expires ${String(summary.paymentMethod.expMonth).padStart(2, "0")}/${summary.paymentMethod.expYear}`
                    : " is about to expire"}
                  . Update it before your next charge to keep your subscription active.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleManagePayment(summary)}
                disabled={managePaymentMutation.isPending || checkoutMutation.isPending}
                data-testid="button-update-card"
              >
                {(managePaymentMutation.isPending || checkoutMutation.isPending) ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Update card
              </Button>
            </div>
          </Card>
        )}

        {!isV3 && summary.subscriptionStatus === "past_due" && (
          <Card className="p-4 border-amber-300 bg-amber-50" data-testid="banner-past-due">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-amber-900 text-sm">Payment failed</p>
                <p className="text-xs text-amber-800 mt-0.5">
                  Your most recent payment didn't go through. Update your card to keep your account active.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleManagePayment(summary)}
                disabled={managePaymentMutation.isPending || checkoutMutation.isPending}
                data-testid="button-fix-payment-method"
              >
                {(managePaymentMutation.isPending || checkoutMutation.isPending) ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Fix payment method
              </Button>
            </div>
          </Card>
        )}

        {!isV3 && ["active", "trialing"].includes(summary.subscriptionStatus) && summary.scheduledPlan === "Host" && (summary.scheduledChangeAt || summary.subscriptionPeriodEnd) && (
          <Card className="p-4 border-red-200 bg-red-50" data-testid="banner-canceling">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-red-700 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-red-900 text-sm">Subscription canceling</p>
                <p className="text-xs text-red-800 mt-0.5" data-testid="text-cancel-effective-date">
                  Your subscription ends on {formatDate(summary.scheduledChangeAt ?? summary.subscriptionPeriodEnd)}. Reactivate to keep your plan active.
                </p>
                {!summary.scheduledChangeReminderSentAt && (
                  <p className="text-xs text-red-700 mt-1" data-testid="text-cancel-reminder-hint">
                    We'll email you about {summary.scheduledChangeReminderLeadDays} days before this takes effect.
                  </p>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => reactivateMutation.mutate()}
                disabled={reactivateMutation.isPending}
                data-testid="button-reactivate-subscription"
              >
                {reactivateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Reactivate
              </Button>
            </div>
          </Card>
        )}

        {!isV3 && (summary.scheduledPlan || summary.scheduledCycle) && summary.scheduledPlan !== "Host" && (
          <ScheduledChangeBanner
            summary={summary}
            onCancelScheduled={(type) => {
              if (type === "plan") changePlanMutation.mutate({ plan: summary.plan, scheduled: false });
              if (type === "cycle") changeCycleMutation.mutate({ cycle: summary.cycle, scheduled: false });
            }}
          />
        )}

        {!isCompedOrFounder && !isModular && !isV3 && (
          <PlanComparisonSection
            currentPlan={summary.plan}
            cycle={summary.cycle}
            onUpgrade={(plan) => setUpgradeTarget(plan)}
            onDowngrade={(plan) => setDowngradeTarget(plan)}
            isPending={changePlanMutation.isPending}
          />
        )}

        {!isV3 && (
          <PaymentMethodCard
            summary={summary}
            onManagePayment={() => handleManagePayment(summary)}
            isManaging={managePaymentMutation.isPending || checkoutMutation.isPending}
          />
        )}

        {!isV3 && !isFreePlan && (summary.pendingProrationRooms > 0 || summary.pendingProrationCreditCents > 0) && (
          <UpcomingChargesCard summary={summary} />
        )}

        {(creditsQuery.data?.activeCreditCents ?? 0) > 0 && (
          <Card className="p-4 border-green-200 bg-green-50">
            <div className="flex items-start gap-3">
              <Gift className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-green-800 text-sm">You have referral credits</p>
                <p className="text-xs text-green-700 mt-0.5">
                  ${((creditsQuery.data?.activeCreditCents ?? 0) / 100).toFixed(2)} in active bill credits will be applied automatically to your next invoice.
                </p>
                {creditsQuery.data && creditsQuery.data.credits.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {creditsQuery.data.credits.map((c) => (
                      <div key={c.id} className="flex items-center justify-between text-xs text-green-700" data-testid={`text-credit-${c.id}`}>
                        <span className="capitalize">{c.creditType.replace(/_/g, " ")}</span>
                        <span className="font-medium">${(c.amountCents / 100).toFixed(2)} · expires {format(new Date(c.expiresAt), "MMM d, yyyy")}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        <BillingHistoryTable history={history} isLoading={historyQuery.isLoading} />

        {!isV3 && !isFreePlan && !isCompedOrFounder && ["active", "trialing"].includes(summary.subscriptionStatus) && (
          <div className="pt-2">
            <button
              onClick={() => setCancelOpen(true)}
              className="text-sm text-red-500 hover:text-red-700 underline"
              data-testid="button-cancel-subscription"
            >
              Cancel subscription
            </button>
          </div>
        )}
      </div>

      <UpgradeDialog
        open={!!upgradeTarget}
        onOpenChange={(o) => {
          if (!o) {
            setUpgradeTarget(null);
            setPlanFieldErrors({});
            setCycleFieldErrors({});
            setPendingCombinedUpgrade(false);
          }
        }}
        currentPlan={summary.plan}
        targetPlan={upgradeTarget}
        cycle={summary.cycle}
        billedRooms={summary.billedRooms}
        seamLockUsage={summary.seamLockUsage ?? null}
        planFieldErrors={planFieldErrors}
        cycleFieldErrors={cycleFieldErrors}
        onConfirm={({ switchToAnnual }) => {
          if (!upgradeTarget) return;
          setPlanFieldErrors({});
          setCycleFieldErrors({});
          const willSwitchCycle = switchToAnnual && summary.cycle === "monthly";
          // Free → paid: no Stripe subscription yet, so route through Checkout.
          // The change-plan endpoint requires an existing subscription.
          if (!summary.stripeSubscriptionId) {
            const cycle = willSwitchCycle ? "annual" : (summary.cycle === "annual" ? "annual" : "monthly");
            checkoutMutation.mutate({ plan: upgradeTarget, cycle, promoCode: pendingPromoCode && !pendingPromoInvalid ? pendingPromoCode : undefined });
            return;
          }
          setPendingCombinedUpgrade(willSwitchCycle);
          changePlanMutation.mutate(
            { plan: upgradeTarget, scheduled: false },
            {
              onSuccess: () => {
                if (willSwitchCycle) {
                  changeCycleMutation.mutate({ cycle: "annual", scheduled: false });
                }
              },
            }
          );
        }}
        isPending={changePlanMutation.isPending || changeCycleMutation.isPending || checkoutMutation.isPending}
        promoInvalid={!summary.stripeSubscriptionId && pendingPromoInvalid}
      />

      <DowngradeDialog
        open={!!downgradeTarget}
        onOpenChange={(o) => {
          if (!o) {
            setDowngradeTarget(null);
            setPlanFieldErrors({});
          }
        }}
        currentPlan={summary.plan}
        targetPlan={downgradeTarget}
        periodEnd={summary.subscriptionPeriodEnd}
        planFieldErrors={planFieldErrors}
        noActiveSubscription={!summary.stripeSubscriptionId && !!downgradeTarget && isGrowthOrProfessionalTier(downgradeTarget)}
        onConfirm={() => {
          if (!downgradeTarget) return;
          setPlanFieldErrors({});
          // Comped/manually-activated users have subscriptionStatus 'active'
          // but no underlying Stripe subscription, so the change-plan endpoint
          // can't operate. Mirror the upgrade fallback at lines 663-667 and
          // route through Checkout for the target plan — but only when the
          // target is a self-serve paid recurring tier (Growth/Professional).
          // Free targets (Host/Research) and custom tiers (Enterprise/Founder)
          // can't be reached via checkout-session.
          if (!summary.stripeSubscriptionId) {
            if (isGrowthOrProfessionalTier(downgradeTarget)) {
              const cycle = summary.cycle === "annual" ? "annual" : "monthly";
              checkoutMutation.mutate({ plan: downgradeTarget, cycle, promoCode: pendingPromoCode && !pendingPromoInvalid ? pendingPromoCode : undefined });
              return;
            }
            toast({
              title: "Contact support to switch plans",
              description: "Your plan needs to be adjusted by our team. Please reach out to support to downgrade to this plan.",
            });
            setDowngradeTarget(null);
            return;
          }
          changePlanMutation.mutate({ plan: downgradeTarget, scheduled: true });
        }}
        isPending={changePlanMutation.isPending || checkoutMutation.isPending}
        promoInvalid={pendingPromoInvalid}
      />

      <CycleChangeDialog
        open={!!cycleTarget}
        onOpenChange={(o) => {
          if (!o) {
            setCycleTarget(null);
            setCycleFieldErrors({});
          }
        }}
        currentCycle={summary.cycle}
        targetCycle={cycleTarget}
        periodEnd={summary.subscriptionPeriodEnd}
        monthlyTotal={summary.monthlyTotal}
        annualTotal={summary.annualTotal}
        seamLockUsage={summary.seamLockUsage ?? null}
        cycleFieldErrors={cycleFieldErrors}
        onConfirm={() => {
          if (!cycleTarget) return;
          setCycleFieldErrors({});
          const scheduled = summary.cycle === "annual" && cycleTarget === "monthly";
          changeCycleMutation.mutate({ cycle: cycleTarget, scheduled });
        }}
        isPending={changeCycleMutation.isPending}
        formatCurrency={formatCurrency}
      />

      <CancelDialog
        open={cancelOpen}
        onOpenChange={(o) => {
          setCancelOpen(o);
          if (!o) cancelMutation.reset();
        }}
        errorMessage={cancelMutation.error ? parseErrorMessage(cancelMutation.error, "Failed to cancel subscription.") : null}
        onConfirm={() => cancelMutation.mutate()}
        isPending={cancelMutation.isPending}
      />
    </AppLayout>
  );
}

// ─── Task #1828 — Promo Code Section ──────────────────────────────────────────

type ActivePromoData = {
  id: number;
  promoCodeId: number;
  appliedAt: string;
  code: string;
  discountType: "percent" | "fixed";
  discountValue: string;
  duration: "forever" | "repeating";
  durationMonths: number | null;
  expiresAt: string | null;
};

type ValidatedPromoPreview = {
  valid: true;
  code: string;
  discountType: "percent" | "fixed";
  discountValue: string;
  duration: "forever" | "repeating";
  durationMonths: number | null;
};

export function PromoCodeSection({
  subscriptionId,
  subscriptionStatus,
  summaryActivePromo,
  summary,
  pendingCode,
  onPendingCodeChange,
  onPendingCodeValidityChange,
}: {
  subscriptionId: string | null;
  subscriptionStatus: string;
  summaryActivePromo: BillingSummary["activePromo"] | null;
  summary: BillingSummary;
  pendingCode: string;
  onPendingCodeChange: (code: string) => void;
  onPendingCodeValidityChange?: (invalid: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [code, setCode] = useState("");
  const hasActiveSub = !!subscriptionId && subscriptionStatus !== "canceled";

  // Validate a staged (not-yet-checked-out) code so non-subscribers see the discount
  // applied to their selected plan/cycle before clicking through to Stripe Checkout.
  // Keyed on the staged code so it always reflects what will be sent at checkout.
  const previewQuery = useQuery<ValidatedPromoPreview>({
    queryKey: ["/api/app/billing/validate-promo", pendingCode],
    queryFn: async () => {
      const res = await apiRequest("POST", "/api/app/billing/validate-promo", { code: pendingCode });
      return res.json();
    },
    enabled: !hasActiveSub && !!pendingCode,
    retry: false,
    staleTime: 60_000,
  });

  // Lift the staged-code validity up to the billing page so checkout/upgrade
  // actions can be blocked while the previewed code is known-invalid. A code is
  // only "invalid" once its preview request has actually errored — never while
  // it's still loading or when there's no staged code at all.
  const stagedCodeInvalid = !hasActiveSub && !!pendingCode && previewQuery.isError;
  useEffect(() => {
    onPendingCodeValidityChange?.(stagedCodeInvalid);
  }, [stagedCodeInvalid, onPendingCodeValidityChange]);
  useEffect(() => {
    return () => onPendingCodeValidityChange?.(false);
  }, [onPendingCodeValidityChange]);

  const activePromoQuery = useQuery<ActivePromoData | null>({
    queryKey: ["/api/app/billing/active-promo"],
  });

  const applyMutation = useMutation({
    mutationFn: async (codeStr: string) => {
      const res = await apiRequest("POST", "/api/app/billing/apply-promo", { code: codeStr });
      return res.json();
    },
    onSuccess: (data: { message: string }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/billing/active-promo"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/billing/summary"] });
      setCode("");
      toast({ title: "Promo applied!", description: data.message });
    },
    onError: (err: unknown) => {
      toast({
        title: "Promo code error",
        description: parseErrorMessage(err, "Could not apply promo code."),
        variant: "destructive",
      });
    },
  });

  const activePromo = activePromoQuery.data ?? (summaryActivePromo
    ? ({
        id: 0,
        promoCodeId: 0,
        appliedAt: "",
        code: summaryActivePromo.code,
        discountType: summaryActivePromo.discountType,
        discountValue: summaryActivePromo.discountValue,
        duration: summaryActivePromo.duration,
        durationMonths: summaryActivePromo.durationMonths,
        expiresAt: summaryActivePromo.expiresAt,
      } as ActivePromoData)
    : null);

  if (activePromoQuery.isLoading && !summaryActivePromo) {
    return <Skeleton className="h-16 w-full rounded-xl" data-testid="skeleton-promo-loading" />;
  }

  if (activePromo) {
    return (
      <Card className="px-5 py-3" data-testid="card-promo-section">
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="text-active-promo-code">
          <Tag className="h-3.5 w-3.5" />
          <span>
            Promo <span className="font-mono tracking-wider text-foreground">{activePromo.code}</span> applied
            {activePromo.expiresAt && (
              <> · expires {new Date(activePromo.expiresAt).toLocaleDateString()}</>
            )}
          </span>
        </div>
      </Card>
    );
  }

  // Non-subscriber: staged code shows a confirmation banner with a live discount preview
  if (!hasActiveSub && pendingCode) {
    const preview = previewQuery.data;
    const isInvalid = previewQuery.isError;
    const isChecking = previewQuery.isLoading;
    // A 409 means the host already holds this exact code on their account — it's
    // not a bad code, so surface it distinctly from "not found"/"expired" errors.
    const isDuplicate = isInvalid && parseErrorStatus(previewQuery.error) === 409;
    // The duplicate IS the currently-applied code, so prefer the known active
    // code (summary/active-promo query) and fall back to the staged code.
    const currentlyAppliedCode =
      activePromoQuery.data?.code ?? summaryActivePromo?.code ?? pendingCode;

    // Monthly-equivalent gross for the selected plan/cycle (annual is shown per-month).
    const grossMonthly = computeGrossMonthly(summary);

    let discountedMonthly: number | null = null;
    let discountLabel: string | null = null;
    let durationLabel = "";
    if (preview) {
      const val = Number(preview.discountValue);
      discountLabel =
        preview.discountType === "percent"
          ? `${preview.discountValue}% off`
          : `${formatCurrency(val)} off`;
      durationLabel =
        preview.duration === "forever"
          ? "forever"
          : `for the first ${preview.durationMonths} month${preview.durationMonths === 1 ? "" : "s"}`;
      discountedMonthly = computeDiscountedMonthly(preview.discountType, preview.discountValue, grossMonthly);
    }

    const cardClass = isDuplicate
      ? "px-5 py-3 border-amber-200 bg-amber-50"
      : isInvalid
      ? "px-5 py-3 border-red-200 bg-red-50"
      : "px-5 py-3 border-emerald-200 bg-emerald-50";

    return (
      <Card className={cardClass} data-testid="card-promo-section">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {isDuplicate ? (
              <div className="flex items-start gap-2 text-xs text-amber-800" data-testid="text-promo-preview-duplicate">
                <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>
                  You've already applied{" "}
                  <span className="font-mono tracking-wider font-semibold">{currentlyAppliedCode}</span>{" "}
                  to your account — no need to add it again.
                </span>
              </div>
            ) : isInvalid ? (
              <div className="flex items-start gap-2 text-xs text-red-700" data-testid="text-promo-preview-error">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>
                  {parseErrorMessage(previewQuery.error, `Code "${pendingCode}" can't be applied.`)} Remove it and try another code.
                </span>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 text-xs text-emerald-700" data-testid="text-pending-promo-code">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    Code <span className="font-mono tracking-wider font-semibold">{pendingCode}</span> will be applied at checkout
                  </span>
                </div>
                {isChecking ? (
                  <p className="text-xs text-emerald-700/80 mt-1 pl-[1.375rem]" data-testid="text-promo-preview-checking">
                    Checking your discount…
                  </p>
                ) : discountLabel ? (
                  <p className="text-xs text-emerald-800 mt-1 pl-[1.375rem]" data-testid="text-promo-preview">
                    <span className="font-semibold">{discountLabel}</span> {durationLabel}
                    {discountedMonthly !== null && grossMonthly > 0 && (
                      <>
                        {" → "}
                        <span className="font-semibold">{formatCurrency(discountedMonthly)}/mo</span>{" "}
                        <span className="text-emerald-700/70 line-through" data-testid="text-promo-preview-original">
                          {formatCurrency(grossMonthly)}/mo
                        </span>
                      </>
                    )}
                  </p>
                ) : null}
              </>
            )}
          </div>
          <button
            onClick={() => onPendingCodeChange("")}
            className={`ml-2 shrink-0 ${isDuplicate ? "text-amber-600 hover:text-amber-800" : isInvalid ? "text-red-600 hover:text-red-800" : "text-emerald-600 hover:text-emerald-800"}`}
            data-testid="button-clear-pending-promo"
            aria-label="Remove promo code"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </Card>
    );
  }

  const handleApply = () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    if (hasActiveSub) {
      applyMutation.mutate(trimmed);
    } else {
      onPendingCodeChange(trimmed);
      setCode("");
    }
  };

  return (
    <Card className="p-5" data-testid="card-promo-section">
      <div className="flex items-center gap-2 mb-3">
        <Tag className="h-4 w-4 text-muted-foreground" />
        <h3 className="font-semibold text-sm">Promo Code</h3>
      </div>

      <div className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Have a promo code? Enter it below to apply a discount to your subscription.
        </p>
        <div className="flex gap-2">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ENTER CODE"
            className="font-mono uppercase tracking-wider max-w-[220px]"
            disabled={applyMutation.isPending}
            onKeyDown={(e) => {
              if (e.key === "Enter" && code.trim()) handleApply();
            }}
            data-testid="input-promo-code-apply"
          />
          <Button
            variant="outline"
            size="sm"
            disabled={!code.trim() || applyMutation.isPending}
            onClick={handleApply}
            data-testid="button-apply-promo"
          >
            {applyMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
          </Button>
        </div>
        {!hasActiveSub && (
          <p className="text-xs text-muted-foreground italic" data-testid="text-promo-at-checkout">
            Code will be applied when you subscribe.
          </p>
        )}
      </div>
    </Card>
  );
}

function FounderOverageBillingPanel({ summary }: { summary: BillingSummary }) {
  const freeRooms = summary.founderFreeRooms ?? 0;
  const overageRooms = summary.founderOverageRooms ?? 0;
  const rateMonthly = summary.founderOverageRateMonthly ?? 0;
  const monthlyCharge = summary.founderOverageMonthlyCharge ?? 0;
  const hasCard = !!summary.stripeCustomerId && !!summary.paymentMethod?.last4;
  const cycleLabel = summary.cycle === "annual" ? "annual" : "monthly";
  const annualCharge = overageRooms * rateMonthly * 12;

  return (
    <Card className="p-5 border-brand/30 bg-brand-muted/60 space-y-4" data-testid="card-founder-overage-billing">
      <div className="flex items-start gap-3">
        <Gift className="h-5 w-5 text-brand mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-brand text-sm" data-testid="text-founder-billing-title">
            Founders Circle — Membership Active
          </p>
          <p className="text-xs text-blue-700 mt-0.5">
            {freeRooms} lifetime-free rooms included. Overage rooms are billed at the Professional rate −20%.
          </p>
        </div>
        <Badge className="bg-brand text-white shrink-0" data-testid="badge-founder-active">Founder</Badge>
      </div>

      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="rounded-lg bg-white border border-blue-100 p-3">
          <p className="text-xs text-gray-500 mb-1">Free rooms</p>
          <p className="text-2xl font-bold text-brand" data-testid="text-founder-free-rooms">{freeRooms}</p>
        </div>
        <div className="rounded-lg bg-white border border-blue-100 p-3">
          <p className="text-xs text-gray-500 mb-1">Active rooms</p>
          <p className="text-2xl font-bold text-gray-900" data-testid="text-founder-billed-rooms">{summary.billedRooms}</p>
        </div>
        <div className={`rounded-lg border p-3 ${overageRooms > 0 ? "bg-amber-50 border-amber-200" : "bg-white border-blue-100"}`}>
          <p className="text-xs text-gray-500 mb-1">Overage rooms</p>
          <p className={`text-2xl font-bold ${overageRooms > 0 ? "text-amber-700" : "text-gray-400"}`} data-testid="text-founder-overage-rooms">{overageRooms}</p>
        </div>
      </div>

      {overageRooms > 0 && (
        <div className="rounded-lg bg-white border border-amber-200 p-4 space-y-2">
          <p className="text-sm font-semibold text-gray-800">Overage billing estimate</p>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">{overageRooms} overage rooms × ${rateMonthly.toFixed(2)}/room/mo</span>
            <span className="font-semibold text-gray-900" data-testid="text-founder-overage-monthly">${monthlyCharge.toFixed(2)}/mo</span>
          </div>
          {cycleLabel === "annual" && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Annual total</span>
              <span className="font-semibold text-gray-900" data-testid="text-founder-overage-annual">${annualCharge.toFixed(2)}/yr</span>
            </div>
          )}
          <p className="text-xs text-gray-500">Billed {cycleLabel}. Charges begin when a card is on file.</p>
        </div>
      )}

      {overageRooms === 0 && (
        <div className="text-sm text-gray-600 bg-white rounded-lg border border-blue-100 p-3" data-testid="text-founder-no-overage">
          You&apos;re within your {freeRooms}-room free allotment. No overage charges.
        </div>
      )}

      {summary.founderOveragePaymentFailed && (
        <div className="flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4" data-testid="banner-founder-overage-payment-failed">
          <AlertTriangle className="h-4 w-4 text-red-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-red-900">Overage payment failed</p>
            <p className="text-xs text-red-800 mt-0.5">
              Your last overage billing charge was declined. Update your card to resume automatic overage billing — your regular plan access is unaffected.
            </p>
          </div>
          <a
            href="#payment-method"
            className="shrink-0 text-xs font-semibold text-red-700 underline underline-offset-2 hover:text-red-900"
            data-testid="link-founder-overage-fix-payment"
          >
            Update card
          </a>
        </div>
      )}

      {!hasCard && !summary.founderOveragePaymentFailed && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="banner-founder-no-card">
          <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">Card required for overage billing</p>
            <p className="text-xs text-amber-800 mt-0.5">
              Add a card below so overage rooms can be billed automatically. Without a card, rooms above your {freeRooms}-room allotment cannot be charged.
            </p>
          </div>
        </div>
      )}
    </Card>
  );
}

function CurrentPlanCard({
  summary,
  isFreePlan,
  isEnterprisePlan,
  statusStyle,
  statusLabel,
  onToggleCycle,
  isCycleChanging,
  hideCycleToggle,
}: {
  summary: BillingSummary;
  isFreePlan: boolean;
  isEnterprisePlan: boolean;
  statusStyle: string;
  statusLabel: string;
  onToggleCycle: () => void;
  isCycleChanging: boolean;
  hideCycleToggle?: boolean;
}) {
  const PlanIcon = PLAN_ICONS[summary.plan] || Building2;
  const isFlatTier = isFlatPricedTier(summary.plan);
  const flatPrice = flatPlanMonthlyPriceUsd(summary.plan);
  const planMonthlyEquivalent =
    summary.cycle === "annual" ? summary.annualTotal / 12 : summary.monthlyTotal;
  const seamLockMonthly = (summary.seamLockUsage?.totalCents ?? 0) / 100;
  const seamLockCount = summary.seamLockUsage?.seamLockCount ?? 0;
  const hasSeamLocks = seamLockCount > 0;
  const displayedTotal = planMonthlyEquivalent + seamLockMonthly;
  const displayedAnnual = summary.annualTotal;
  const isAnnualCycle = summary.cycle === "annual";
  const monthlyCyclePrice = MONTHLY_PRICES_PER_ROOM[summary.plan] ?? null;
  const showBreakdown =
    !isFreePlan &&
    !isEnterprisePlan &&
    summary.pricePerRoom !== null &&
    summary.chargeableRooms > 0;
  const monthlyEquivalentTotal =
    monthlyCyclePrice !== null ? monthlyCyclePrice * summary.chargeableRooms : null;
  const annualSavings =
    isAnnualCycle && monthlyEquivalentTotal !== null
      ? Math.max(0, monthlyEquivalentTotal * 12 - summary.annualTotal)
      : 0;
  const roomLabel = `${summary.chargeableRooms} room${summary.chargeableRooms !== 1 ? "s" : ""}`;
  const allInMonthlyTotal = summary.monthlyTotal + seamLockMonthly;
  const activePromo = summary.activePromo ?? null;
  const promoDiscountedDisplayedTotal =
    activePromo && activePromo.discountedMonthlyTotal != null
      ? (isAnnualCycle && activePromo.discountedAnnualTotal != null
          ? activePromo.discountedAnnualTotal / 12
          : activePromo.discountedMonthlyTotal)
      : null;
  const promoDiscountedAnnual =
    activePromo && activePromo.discountedAnnualTotal != null
      ? activePromo.discountedAnnualTotal
      : null;
  const promoDurationLabel = activePromo
    ? activePromo.duration === "forever"
      ? "forever"
      : `for ${activePromo.durationMonths ?? 1} month${activePromo.durationMonths === 1 ? "" : "s"}`
    : null;
  const promoDiscountLabel = activePromo
    ? activePromo.discountType === "percent"
      ? `${Number(activePromo.discountValue)}% off`
      : `$${Number(activePromo.discountValue).toFixed(2)} off`
    : null;

  return (
    <Card className="p-6" data-testid="card-current-plan">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center">
            <PlanIcon className="h-5 w-5 text-brand" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-gray-900" data-testid="text-plan-name" data-tour="billing-invoices">{planDisplayName(summary.plan)} Plan</h2>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusStyle}`} data-testid="badge-subscription-status">
                {statusLabel || "none"}
              </span>
              {activePromo && (
                <span
                  className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1"
                  data-testid="badge-savings-applied"
                  title={`Promo ${activePromo.code}: ${promoDiscountLabel} ${promoDurationLabel}`}
                >
                  <Tag className="h-3 w-3" />
                  Savings applied · {promoDiscountLabel}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              {isFreePlan
                ? "Free — 1 Buy Box search/day (up to 100 results)"
                : isFlatTier && flatPrice !== null
                ? `$${flatPrice}/mo flat · unlimited search`
                : summary.pricePerRoom === null
                  ? `${summary.billedRooms} room${summary.billedRooms !== 1 ? "s" : ""} — Contact us for pricing`
                  : `${summary.billedRooms} room${summary.billedRooms !== 1 ? "s" : ""} × $${summary.pricePerRoom}/room/mo`}
            </p>
            {summary.shadowRooms > 0 && (
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p
                      className="text-xs text-gray-500 mt-1 inline-flex items-center gap-1 cursor-help underline decoration-dotted underline-offset-2"
                      data-testid="text-shadow-rooms-notice"
                    >
                      {summary.shadowRooms} shadow bedroom{summary.shadowRooms !== 1 ? "s" : ""} excluded
                    </p>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-xs">
                    Shadow bedrooms represent secondary occupants sharing a room with a primary tenant. They aren't billed separately from the primary bedroom.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>

      </div>

      {(!isFreePlan || isEnterprisePlan) && (
        <div className="mt-5 pt-5 border-t border-gray-100 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-gray-500 font-medium">Billed rooms</p>
            <p className="text-xl font-bold text-gray-900 mt-0.5" data-testid="text-billed-rooms">{summary.billedRooms}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">Monthly cost</p>
            {isEnterprisePlan ? (
              <p className="text-base font-semibold text-purple-700 mt-0.5" data-testid="text-monthly-total">Contact Us</p>
            ) : activePromo && promoDiscountedDisplayedTotal != null ? (
              <div className="mt-0.5">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <p className="text-xl font-bold text-emerald-700" data-testid="text-monthly-total">{formatCurrency(promoDiscountedDisplayedTotal)}</p>
                  <p className="text-sm text-gray-400 line-through" data-testid="text-monthly-total-original">{formatCurrency(displayedTotal)}</p>
                </div>
              </div>
            ) : (
              <>
                <p className="text-xl font-bold text-gray-900 mt-0.5" data-testid="text-monthly-total">{formatCurrency(displayedTotal)}</p>
                {hasSeamLocks && (
                  <p className="text-[11px] text-gray-500 mt-0.5" data-testid="text-monthly-total-includes-locks">
                    Includes {formatCurrency(seamLockMonthly)} smart lock add-on
                  </p>
                )}
              </>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">Annual cost</p>
            {isEnterprisePlan ? (
              <p className="text-base font-semibold text-purple-700 mt-0.5" data-testid="text-annual-total">Contact Us</p>
            ) : activePromo && promoDiscountedAnnual != null ? (
              <div className="mt-0.5">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <p className="text-xl font-bold text-emerald-700" data-testid="text-annual-total">{formatCurrency(promoDiscountedAnnual)}</p>
                  <p className="text-sm text-gray-400 line-through" data-testid="text-annual-total-original">{formatCurrency(displayedAnnual)}</p>
                </div>
              </div>
            ) : (
              <p className="text-xl font-bold text-gray-900 mt-0.5" data-testid="text-annual-total">{formatCurrency(displayedAnnual)}</p>
            )}
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">Next renewal</p>
            <p className="text-sm font-medium text-gray-900 mt-0.5" data-testid="text-next-renewal">
              {formatDate(summary.subscriptionPeriodEnd)}
            </p>
          </div>
        </div>
      )}
      {showBreakdown && (
        <div
          className="mt-4 pt-4 border-t border-gray-100"
          data-testid="section-billing-breakdown"
        >
          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
            How this is calculated
          </p>
          <p className="text-sm text-gray-700 mt-2" data-testid="text-billing-breakdown-summary">
            {planDisplayName(summary.plan)} plan, {isAnnualCycle ? "annual" : "monthly"} cycle:
            {" "}
            <span className="font-medium">
              ${summary.pricePerRoom}/room/mo × {roomLabel} = {formatCurrency(summary.monthlyTotal)}/mo
            </span>
            {hasSeamLocks && (
              <>
                {" "}plus{" "}
                <span className="font-medium" data-testid="text-billing-breakdown-seam-locks">
                  {seamLockCount} smart lock{seamLockCount !== 1 ? "s" : ""} × ${(summary.seamLockUsage!.unitPriceCents / 100).toFixed(2)}/mo = {formatCurrency(seamLockMonthly)}/mo
                </span>
                , for an all-in monthly total of{" "}
                <span className="font-semibold text-gray-900" data-testid="text-billing-breakdown-all-in-monthly">
                  {formatCurrency(allInMonthlyTotal)}/mo
                </span>
              </>
            )}
            {isAnnualCycle && (
              <>
                {hasSeamLocks ? " (" : ""}
                {hasSeamLocks ? "plan " : ""}billed yearly as{" "}
                <span className="font-medium" data-testid="text-billing-breakdown-annual">
                  {formatCurrency(summary.annualTotal)}
                </span>
                {hasSeamLocks ? "; smart locks are billed monthly based on usage)" : ""}
              </>
            )}
            .
          </p>
          {isAnnualCycle && monthlyEquivalentTotal !== null && monthlyCyclePrice !== null && (
            <p
              className="text-sm text-emerald-700 mt-2"
              data-testid="text-billing-breakdown-savings"
            >
              You're saving{" "}
              <span className="font-semibold">{formatCurrency(annualSavings)}/yr</span>{" "}
              vs. paying month-to-month (${monthlyCyclePrice}/room/mo ={" "}
              {formatCurrency(monthlyEquivalentTotal * 12)}/yr).
            </p>
          )}
        </div>
      )}
      {(summary.seamLockUsage?.seamLockCount ?? 0) > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100" data-testid="section-seam-lock-usage">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Smart Lock Add-ons</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {summary.seamLockUsage!.seamLockCount} non-TTLock lock{summary.seamLockUsage!.seamLockCount !== 1 ? "s" : ""} × ${(summary.seamLockUsage!.unitPriceCents / 100).toFixed(2)}/lock/month
              </p>
            </div>
            <p className="text-sm font-semibold text-gray-900" data-testid="text-seam-lock-total">
              {formatCurrency(summary.seamLockUsage!.totalCents / 100)}/mo
            </p>
          </div>
        </div>
      )}

      {!isFreePlan && !hideCycleToggle && (
        <div className="mt-4 flex items-center gap-3">
          <span className="text-sm text-gray-600">Billing cycle:</span>
          <div className="flex items-center gap-2 bg-gray-100 rounded-full p-1">
            <button
              onClick={() => summary.cycle !== "monthly" && onToggleCycle()}
              disabled={isCycleChanging || summary.cycle === "monthly"}
              className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${summary.cycle === "monthly" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
              data-testid="button-cycle-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => summary.cycle !== "annual" && onToggleCycle()}
              disabled={isCycleChanging || summary.cycle === "annual"}
              className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${summary.cycle === "annual" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
              data-testid="button-cycle-annual"
            >
              Annual <span className="text-green-600 font-bold">~17% off</span>
            </button>
          </div>
          {isCycleChanging && <RefreshCw className="h-3 w-3 animate-spin text-gray-400" />}
        </div>
      )}
    </Card>
  );
}

function PaymentMethodCard({
  summary,
  onManagePayment,
  isManaging,
}: {
  summary: BillingSummary;
  onManagePayment: () => void;
  isManaging: boolean;
}) {
  const hasPaymentMethod = summary.paymentMethod && summary.paymentMethod.last4;

  // Surface Stripe environment + customer ID so support can quickly verify
  // the host is on the right Stripe account (test vs live) and copy their
  // cus_… into the dashboard. Failures from the probe are silent — the
  // diagnostics row just doesn't render.
  const stripeConfigQuery = useQuery<StripeConfig>({
    queryKey: ["/api/app/billing/stripe-config"],
  });
  const stripeMode = stripeConfigQuery.data?.mode ?? "unknown";
  const modeLabel =
    stripeMode === "test" ? "Test mode" :
    stripeMode === "live" ? "Live mode" :
    "Mode unknown";
  const modeBadgeClass =
    stripeMode === "test" ? "bg-amber-100 text-amber-800 border-amber-200" :
    stripeMode === "live" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
    "bg-gray-100 text-gray-700 border-gray-200";

  return (
    <Card className="p-6" data-testid="card-payment-method">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
            <CreditCard className="h-5 w-5 text-gray-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">Payment Method</h2>
            {hasPaymentMethod ? (
              <p className="text-sm text-gray-500" data-testid="text-payment-method">
                {summary.paymentMethod!.brand?.toUpperCase() || "Card"} ending in {summary.paymentMethod!.last4}
              </p>
            ) : (
              <p className="text-sm text-gray-400" data-testid="text-payment-method">No payment method on file</p>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onManagePayment}
          disabled={isManaging}
          data-testid="button-manage-payment"
        >
          {isManaging ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          {hasPaymentMethod ? "Manage Payment" : "Add Payment Method"}
        </Button>
      </div>

      <div className="mt-3 flex items-center flex-wrap gap-2 text-xs text-gray-500" data-testid="row-stripe-diagnostics">
        <Badge variant="outline" className={modeBadgeClass} data-testid="badge-stripe-mode">
          Stripe: {modeLabel}
        </Badge>
        {summary.stripeCustomerId ? (
          <span className="font-mono text-gray-600" data-testid="text-stripe-customer-id">
            Customer: {summary.stripeCustomerId}
          </span>
        ) : (
          <span className="text-gray-400" data-testid="text-stripe-customer-id">
            Customer: not yet created
          </span>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Wallet className="h-4 w-4 text-gray-400" />
          <span>Need to set up payment methods for vendors and contractors?</span>
        </div>
        <Link href="/app/operations/vendors/settings">
          <Button variant="ghost" size="sm" className="text-brand hover:text-brand-hover hover:bg-blue-50" data-testid="link-vendor-payment-methods">
            Looking for Payment Methods for Vendors?
            <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </Link>
      </div>
    </Card>
  );
}

function UpcomingChargesCard({ summary }: { summary: BillingSummary }) {
  const prorationCharge = (summary.pendingProrationChargeCents ?? 0) / 100;
  const pendingCreditDollars = (summary.pendingProrationCreditCents ?? 0) / 100;
  const hasPendingCharges = summary.pendingProrationRooms > 0;
  const hasPendingCredits = (summary.pendingProrationCreditCents ?? 0) > 0;
  const settlementDate = summary.nextSettlementAt ? new Date(summary.nextSettlementAt) : null;
  const settlementLabel = settlementDate ? format(settlementDate, "MMM d, yyyy") : null;

  return (
    <Card className="p-6 border-amber-200 bg-amber-50" data-testid="card-upcoming-charges">
      <div className="flex items-center gap-2 mb-3">
        <Receipt className="h-5 w-5 text-amber-600" />
        <h2 className="text-base font-semibold text-amber-800">Upcoming Charges & Credits</h2>
      </div>
      {hasPendingCharges && (
        <>
          <p className="text-sm text-amber-700 mb-3">
            You have <strong>{summary.pendingProrationRooms} room{summary.pendingProrationRooms !== 1 ? "s" : ""}</strong> pending proration that will be billed on your next invoice.
          </p>
          {summary.pendingProrationBreakdown && summary.pendingProrationBreakdown.length > 0 && (
            <ul className="mb-3 space-y-1 pl-2 border-l-2 border-amber-200" data-testid="list-proration-breakdown">
              {summary.pendingProrationBreakdown.map((entry, i) => (
                <li key={entry.propertyId ?? `unknown-${i}`} className="flex items-center justify-between text-xs text-amber-700">
                  <span className="truncate mr-2">{entry.propertyName ?? "Unknown property"}</span>
                  <span className="font-medium whitespace-nowrap" data-testid={`text-proration-charge-amount-${entry.propertyId ?? `unknown-${i}`}`}>
                    {entry.rooms} room{entry.rooms !== 1 ? "s" : ""} · {formatCurrency(entry.amountCents / 100)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center justify-between text-sm">
            <span className="text-amber-700">Proration charge estimate</span>
            <span className="font-semibold text-amber-800" data-testid="text-proration-estimate">{formatCurrency(prorationCharge)}</span>
          </div>
        </>
      )}
      {hasPendingCredits && (
        <div className={hasPendingCharges ? "mt-4 pt-4 border-t border-amber-200" : undefined}>
          <p className="text-sm text-amber-700 mb-3">
            You have <strong>{summary.pendingProrationCreditEvents} pending credit{summary.pendingProrationCreditEvents !== 1 ? "s" : ""}</strong>
            {settlementLabel ? <> that will be applied on <strong>{settlementLabel}</strong>.</> : <> that will be applied on your next invoice.</>}
          </p>
          {summary.pendingProrationCreditBreakdown && summary.pendingProrationCreditBreakdown.length > 0 && (
            <ul className="mb-3 space-y-1 pl-2 border-l-2 border-amber-200" data-testid="list-proration-credit-breakdown">
              {summary.pendingProrationCreditBreakdown.map((entry, i) => (
                <li key={entry.propertyId ?? `unknown-${i}`} className="flex items-center justify-between text-xs text-amber-700">
                  <span className="truncate mr-2">{entry.propertyName ?? "Unknown property"}</span>
                  <span className="font-medium whitespace-nowrap" data-testid={`text-proration-credit-amount-${entry.propertyId ?? `unknown-${i}`}`}>
                    {entry.rooms} room{entry.rooms !== 1 ? "s" : ""} · −{formatCurrency(entry.amountCents / 100)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center justify-between text-sm">
            <span className="text-amber-700">Pending credit total</span>
            <span className="font-semibold text-emerald-700" data-testid="text-pending-credit-total">−{formatCurrency(pendingCreditDollars)}</span>
          </div>
          {settlementLabel && (
            <div className="flex items-center justify-between text-xs text-amber-700 mt-1" data-testid="text-pending-credit-settlement">
              <span>Applied on</span>
              <span className="font-medium">{settlementLabel}</span>
            </div>
          )}
        </div>
      )}
      {summary.cycle === "annual" && summary.subscriptionPeriodEnd && (
        <p className="text-xs text-amber-600 mt-3 pt-2 border-t border-amber-200" data-testid="text-annual-renewal-unchanged">
          These charges and credits are prorated through your current annual period ending{" "}
          <strong>{formatDate(summary.subscriptionPeriodEnd)}</strong>. Your annual renewal date is unchanged.
        </p>
      )}
    </Card>
  );
}

function formatSnapshotMonth(snapshotMonth: string) {
  const [year, month] = snapshotMonth.split("-").map((p) => parseInt(p, 10));
  if (!year || !month) return snapshotMonth;
  const d = new Date(Date.UTC(year, month - 1, 1));
  return format(d, "MMM yyyy");
}

function BillingHistoryTable({ history, isLoading }: { history: BillingHistoryEvent[]; isLoading: boolean }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const total = history.length;
  const safePage = clampPage(page, total, pageSize);
  const startIdx = (safePage - 1) * pageSize;
  const pageItems = history.slice(startIdx, startIdx + pageSize);
  return (
    <Card className="p-6" data-testid="card-billing-history">
      <div className="flex items-center gap-2 mb-4">
        <Receipt className="h-5 w-5 text-gray-600" />
        <h2 className="text-base font-semibold text-gray-900">Billing History</h2>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : history.length === 0 ? (
        <div className="text-center py-8 border border-dashed rounded-lg">
          <Receipt className="mx-auto h-8 w-8 text-gray-300 mb-2" />
          <p className="text-sm text-gray-400">No billing events yet.</p>
          <p className="text-xs text-gray-400 mt-1">Room additions and invoice events will appear here.</p>
        </div>
      ) : (
        <>
        <div className="border rounded-lg overflow-hidden" data-tour="billing-history">
          <table className="w-full text-sm" data-testid="table-billing-history">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left px-4 py-3 font-medium text-gray-500">Date</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Event</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Details</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Amount</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((event) => {
                if (event.type === "seam_lock_charge") {
                  const unitPrice = event.unitPriceCents / 100;
                  const total = event.totalCents / 100;
                  return (
                    <tr key={event.id} className="border-b last:border-0" data-testid={`row-billing-${event.id}`}>
                      <td className="px-4 py-3 text-gray-600" data-testid={`text-billing-date-${event.id}`}>
                        {formatSnapshotMonth(event.snapshotMonth)}
                      </td>
                      <td className="px-4 py-3 text-gray-900" data-testid={`text-billing-event-${event.id}`}>
                        Smart Lock charge
                      </td>
                      <td className="px-4 py-3 text-gray-700" data-testid={`text-billing-details-${event.id}`}>
                        {event.seamLockCount} lock{event.seamLockCount !== 1 ? "s" : ""} × {formatCurrency(unitPrice)}
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-900" data-testid={`text-billing-amount-${event.id}`}>
                        {formatCurrency(total)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">Settled</span>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={event.id} className="border-b last:border-0" data-testid={`row-billing-${event.id}`}>
                    <td className="px-4 py-3 text-gray-600" data-testid={`text-billing-date-${event.id}`}>
                      {formatDate(event.eventAt)}
                    </td>
                    <td className="px-4 py-3 text-gray-900" data-testid={`text-billing-event-${event.id}`}>
                      <span>{event.notes || "Room count adjustment"}</span>
                      {event.propertyName ? (
                        <span className="ml-1 text-gray-500 text-sm" data-testid={`text-billing-property-${event.id}`}>
                          · {event.propertyName}
                        </span>
                      ) : (
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className="ml-1 text-gray-400 text-sm italic cursor-help underline decoration-dotted underline-offset-2"
                                data-testid={`text-billing-property-${event.id}`}
                              >
                                · Property unavailable
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-xs">
                              This row predates per-property tracking, or its property has since been removed from your account.
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700" data-testid={`text-billing-details-${event.id}`}>
                      <span className={event.roomsAdded > 0 ? "text-green-600" : "text-red-600"}>
                        {event.roomsAdded > 0 ? `+${event.roomsAdded}` : event.roomsAdded} room{Math.abs(event.roomsAdded) !== 1 ? "s" : ""}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400" data-testid={`text-billing-amount-${event.id}`}>—</td>
                    <td className="px-4 py-3">
                      {event.processedAt ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">Processed</span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-medium">Pending</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-3">
          <PaginationControls
            page={safePage}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
            testIdPrefix="billing-history"
            itemLabel="events"
          />
        </div>
        </>
      )}
    </Card>
  );
}

function PlanComparisonSection({
  currentPlan,
  cycle,
  onUpgrade,
  onDowngrade,
  isPending,
}: {
  currentPlan: AccountType;
  cycle: string;
  onUpgrade: (plan: AccountType) => void;
  onDowngrade: (plan: AccountType) => void;
  isPending: boolean;
}) {
  return (
    <Card className="p-6" data-testid="section-plan-comparison" data-tour="billing-plan">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Available Plans</h2>
          <p className="text-sm text-gray-500 mt-0.5">Compare plans and switch at any time. Research &amp; Host are flat monthly; Growth &amp; Professional are priced per room per month. Every paid plan includes unlimited search.</p>
          <p className="text-xs text-gray-400 mt-1 italic">Only the account owner or a team member with admin access can change the plan or billing cycle.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {SELECTABLE_PLANS.filter((plan) => !isFounderTier(plan) || plan === currentPlan).map((plan) => {
          const PlanIcon = PLAN_ICONS[plan] || Building2;
          const price = MONTHLY_PRICES_PER_ROOM[plan];
          const planFlatPrice = flatPlanMonthlyPriceUsd(plan);
          const isFlat = planFlatPrice !== null;
          const isCurrent = plan === currentPlan;
          const isFree = !isFlat && price === 0;
          const annualPrice = price != null && price !== 0 ? Math.round(price * 0.833) : null;
          const displayPrice = cycle === "annual" && annualPrice != null ? annualPrice : price;
          const isPaidPlan = !isFree && price != null;
          const planOrder = SELECTABLE_PLANS.indexOf(plan);
          const currentOrder = SELECTABLE_PLANS.indexOf(currentPlan);
          const isUpgrade = planOrder > currentOrder;

          return (
            <div
              key={plan}
              className={`relative border-2 rounded-xl p-4 flex flex-col transition-all ${
                isCurrent
                  ? "border-brand bg-blue-50 shadow-sm"
                  : "border-gray-200 bg-white hover:border-blue-200 hover:shadow-sm"
              }`}
              data-testid={`card-plan-compare-${plan.toLowerCase()}`}
            >
              {isCurrent && (
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                  <span className="bg-brand text-white text-xs font-semibold px-3 py-0.5 rounded-full">
                    Current
                  </span>
                </div>
              )}
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isCurrent ? "bg-brand/10" : "bg-gray-100"}`}>
                  <PlanIcon className={`h-4 w-4 ${isCurrent ? "text-brand" : "text-gray-500"}`} />
                </div>
                <span className="font-semibold text-gray-900 text-sm">{planDisplayName(plan)}</span>
              </div>
              <div className="mb-4 flex-1">
                {isFlat && planFlatPrice !== null ? (
                  <>
                    <p className="text-2xl font-bold text-gray-900" data-testid={`text-plan-price-${plan.toLowerCase()}`}>
                      ${planFlatPrice}
                      <span className="text-xs font-normal text-gray-500">/mo</span>
                    </p>
                    <p className="text-[11px] text-gray-500 mt-1.5" data-testid={`text-plan-flat-${plan.toLowerCase()}`}>
                      Flat monthly · unlimited search
                    </p>
                  </>
                ) : isFree ? (
                  <p className="text-2xl font-bold text-gray-900">Free</p>
                ) : (
                  <>
                    <p className="text-2xl font-bold text-gray-900">
                      ${displayPrice}
                      <span className="text-xs font-normal text-gray-500">/room/mo</span>
                    </p>
                    {cycle === "annual" && annualPrice != null && (
                      <p className="text-xs text-green-600 font-medium mt-0.5">~17% off with annual billing</p>
                    )}
                    {cycle === "monthly" && isPaidPlan && (
                      <p className="text-xs text-gray-400 mt-0.5">Save ~17% with annual</p>
                    )}
                    {isGrowthOrProfessionalTier(plan) && displayPrice != null && (
                      <p className="text-[11px] text-gray-500 mt-1.5" data-testid={`text-plan-min-${plan.toLowerCase()}`}>
                        No room minimum · billed per active room
                      </p>
                    )}
                  </>
                )}
              </div>
              {isCurrent ? (
                <div className="text-center text-xs text-brand font-medium py-1.5 border border-brand/20 rounded-lg bg-white" data-testid={`badge-current-plan-${plan.toLowerCase()}`}>
                  <Check className="inline h-3.5 w-3.5 mr-1" />
                  Your current plan
                </div>
              ) : (
                <Button
                  size="sm"
                  variant={isUpgrade ? "default" : "outline"}
                  className={isUpgrade ? "bg-brand hover:bg-brand-hover text-white w-full" : "w-full"}
                  onClick={() => !isPending && (isUpgrade ? onUpgrade(plan) : onDowngrade(plan))}
                  disabled={isPending}
                  data-testid={`button-plan-${isUpgrade ? "upgrade" : "downgrade"}-${plan.toLowerCase()}`}
                >
                  {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
                    <>
                      {isUpgrade ? "Upgrade" : "Downgrade"}
                      <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </>
                  )}
                </Button>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-gray-400 mt-3 text-center">
        Need Property Manager or PM Pro? <a href="mailto:info@padpulse.io" className="text-brand hover:underline" data-testid="link-enterprise-contact">Contact us</a> for custom pricing.
      </p>
    </Card>
  );
}

export function ScheduledChangeBanner({
  summary,
  onCancelScheduled,
}: {
  summary: BillingSummary;
  onCancelScheduled: (type: "plan" | "cycle") => void;
}) {
  // Fall back to subscription_period_end for accounts that scheduled a
  // change before scheduledChangeAt was being captured.
  const effectiveDate = formatDate(summary.scheduledChangeAt ?? summary.subscriptionPeriodEnd);
  // Hide the heads-up once the reminder marker has been stamped (the
  // email already went out) or once there's no scheduled change left.
  const showReminderHint =
    !summary.scheduledChangeReminderSentAt &&
    Boolean(summary.scheduledChangeAt || summary.subscriptionPeriodEnd);
  return (
    <Card className="p-4 border-amber-200 bg-amber-50" data-testid="banner-scheduled-change">
      <div className="flex items-start gap-3">
        <Clock className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 space-y-2">
          {summary.scheduledPlan && (
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm text-amber-900" data-testid="text-scheduled-plan-effective-date">
                <span className="font-semibold">Plan change scheduled:</span>{" "}
                Your plan will switch from <span className="font-semibold">{planDisplayName(summary.plan)}</span> to{" "}
                <span className="font-semibold">{planDisplayName(summary.scheduledPlan)}</span> on {effectiveDate}.
              </div>
              <button
                onClick={() => onCancelScheduled("plan")}
                className="text-xs text-amber-700 hover:text-amber-900 underline whitespace-nowrap"
                data-testid="button-cancel-scheduled-plan"
              >
                Cancel
              </button>
            </div>
          )}
          {summary.scheduledCycle && (
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm text-amber-900" data-testid="text-scheduled-cycle-effective-date">
                <span className="font-semibold">Cycle change scheduled:</span>{" "}
                You'll stay on <span className="font-semibold">{summary.cycle}</span> until {effectiveDate}, then move to{" "}
                <span className="font-semibold">{summary.scheduledCycle}</span> billing.
              </div>
              <button
                onClick={() => onCancelScheduled("cycle")}
                className="text-xs text-amber-700 hover:text-amber-900 underline whitespace-nowrap"
                data-testid="button-cancel-scheduled-cycle"
              >
                Cancel
              </button>
            </div>
          )}
          {showReminderHint && (
            <p className="text-xs text-amber-700" data-testid="text-scheduled-change-reminder-hint">
              We'll email you about {summary.scheduledChangeReminderLeadDays} days before this takes effect.
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}

export function UpgradeDialog({
  open,
  onOpenChange,
  currentPlan,
  targetPlan,
  cycle,
  billedRooms,
  seamLockUsage,
  planFieldErrors,
  cycleFieldErrors,
  onConfirm,
  isPending,
  promoInvalid = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPlan: AccountType;
  targetPlan: AccountType | null;
  cycle: string;
  billedRooms: number;
  seamLockUsage?: BillingSummary["seamLockUsage"] | null;
  planFieldErrors: Record<string, string>;
  cycleFieldErrors: Record<string, string>;
  onConfirm: (opts: { switchToAnnual: boolean }) => void;
  isPending: boolean;
  promoInvalid?: boolean;
}) {
  const isCurrentlyMonthly = cycle === "monthly";
  const [switchToAnnual, setSwitchToAnnual] = useState(false);
  useEffect(() => {
    if (open) setSwitchToAnnual(isCurrentlyMonthly);
  }, [open, isCurrentlyMonthly]);

  const previewCycle: "monthly" | "annual" = switchToAnnual ? "annual" : (cycle as "monthly" | "annual");
  // Task #1856 — Fetch the exact Stripe-calculated prorated charge for this
  // upgrade so the dialog shows a live amount instead of just the estimated
  // monthly difference. Falls back to the estimate when Stripe preview isn't
  // available (comped users, missing subscription, etc.).
  const previewQuery = useQuery<{ chargedAmountCents: number | null; renewalDate: string | null }>({
    queryKey: ["/api/app/billing/preview-plan-change", targetPlan, previewCycle],
    queryFn: async () => {
      const res = await apiRequest("POST", "/api/app/billing/preview-plan-change", {
        plan: targetPlan,
        cycle: previewCycle,
      });
      return res.json();
    },
    enabled: open && !!targetPlan,
    staleTime: 30_000,
  });

  if (!targetPlan) return null;
  const currentMonthly = MONTHLY_PRICES_PER_ROOM[currentPlan] ?? 0;
  const targetMonthly = MONTHLY_PRICES_PER_ROOM[targetPlan] ?? 0;
  const targetAnnualPerRoom = Math.round(targetMonthly * 0.833);
  const newPricePerRoom = switchToAnnual ? targetAnnualPerRoom : targetMonthly;
  const currentEffectivePerRoom = cycle === "annual" ? Math.round(currentMonthly * 0.833) : currentMonthly;
  const diffPerRoom = newPricePerRoom - currentEffectivePerRoom;
  const targetFeatures = PLAN_FEATURES[targetPlan];
  const currentFeatures = PLAN_FEATURES[currentPlan];
  const newFeatures = targetFeatures
    ? targetFeatures.included.filter((f) => !currentFeatures?.included.includes(f))
    : [];

  const newCycleLabel = switchToAnnual ? "annual" : cycle;
  const annualSavingsPerRoom = targetMonthly - targetAnnualPerRoom;
  const targetChargeableRooms = chargeableRoomsForPlan(targetPlan, billedRooms);
  const currentChargeableRooms = chargeableRoomsForPlan(currentPlan, billedRooms);
  const newMonthlyTotal = newPricePerRoom * targetChargeableRooms;
  const currentMonthlyTotal = currentEffectivePerRoom * currentChargeableRooms;
  const totalMonthlyDiff = newMonthlyTotal - currentMonthlyTotal;
  const seamLockMonthly = (seamLockUsage?.totalCents ?? 0) / 100;
  const seamLockCount = seamLockUsage?.seamLockCount ?? 0;
  const hasSeamLocks = seamLockCount > 0;
  const allInMonthlyTotal = newMonthlyTotal + seamLockMonthly;

  const previewCents = previewQuery.data?.chargedAmountCents;
  const hasPreview = typeof previewCents === "number";
  const previewLoading = previewQuery.isLoading;
  const chargeTodayLabel = hasPreview ? `$${(previewCents! / 100).toFixed(2)}` : null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && isPending) return; onOpenChange(o); }}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="dialog-upgrade"
        onEscapeKeyDown={(e) => { if (isPending) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isPending) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (isPending) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpCircle className="h-5 w-5 text-brand" />
            Upgrade to {targetPlan}
          </DialogTitle>
          <DialogDescription>
            {hasPreview
              ? `Your new plan activates immediately. You'll be charged ${chargeTodayLabel} today (the exact prorated amount for the rest of your current billing period).`
              : previewLoading
                ? "Your new plan activates immediately. Calculating your exact charge for today…"
                : "Your new plan activates immediately. You'll be charged a prorated amount today for the rest of your current billing period."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {(planFieldErrors.plan || planFieldErrors.scheduled) && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="error-upgrade-plan">
              <p className="text-sm text-red-700">{planFieldErrors.plan || planFieldErrors.scheduled}</p>
            </div>
          )}
          {newFeatures.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-2">What you'll gain:</p>
              <ul className="space-y-1.5">
                {newFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                    <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {cycleFieldErrors.cycle && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="error-upgrade-cycle">
              <p className="text-sm text-red-700">{cycleFieldErrors.cycle}</p>
            </div>
          )}

          {isCurrentlyMonthly && (
            <label className="flex items-start gap-3 p-3 border-2 border-green-200 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors" data-testid="toggle-switch-annual">
              <input
                type="checkbox"
                checked={switchToAnnual}
                onChange={(e) => setSwitchToAnnual(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-green-600"
                data-testid="checkbox-switch-annual"
              />
              <div className="flex-1">
                <div className="text-sm font-semibold text-green-900">Also switch to annual billing</div>
                <div className="text-xs text-green-700 mt-0.5">
                  Save ${annualSavingsPerRoom}/room/mo (~17%). Pay yearly, get the lower rate.
                </div>
              </div>
            </label>
          )}

          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Current</span>
              <span className="font-semibold text-gray-900">{planDisplayName(currentPlan)} · ${currentEffectivePerRoom}/room/mo ({cycle})</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">New</span>
              <span className="font-semibold text-gray-900">{planDisplayName(targetPlan)} · ${newPricePerRoom}/room/mo ({newCycleLabel})</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Billed rooms</span>
              <span className="font-semibold text-gray-900">
                {billedRooms}
              </span>
            </div>
            <div className="flex justify-between text-sm pt-1 border-t border-blue-200">
              <span className="text-gray-700 font-medium">Difference per room/mo</span>
              <span className="font-bold text-brand">{diffPerRoom >= 0 ? "+" : ""}${diffPerRoom}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-700 font-medium">Total monthly change</span>
              <span className="font-bold text-brand" data-testid="text-upgrade-total-diff">{totalMonthlyDiff >= 0 ? "+" : ""}${totalMonthlyDiff}</span>
            </div>
            <div className="flex justify-between text-sm pt-1 border-t border-blue-200">
              <span className="text-gray-700 font-medium">
                {hasPreview ? "Charge today" : "Est. charge today"}
              </span>
              <span className="font-bold text-brand" data-testid="text-upgrade-charge-today">
                {previewLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin inline" />
                ) : hasPreview ? (
                  chargeTodayLabel
                ) : (
                  `~$${Math.max(totalMonthlyDiff, 0)}`
                )}
              </span>
            </div>
            {hasSeamLocks && (
              <>
                <div className="flex justify-between text-sm pt-1 border-t border-blue-200">
                  <span className="text-gray-600">New plan ({newCycleLabel === "annual" ? "monthly equivalent" : "monthly"})</span>
                  <span className="font-semibold text-gray-900" data-testid="text-upgrade-plan-monthly">
                    ${Math.round(newMonthlyTotal)}/mo
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Smart lock add-on (billed monthly)</span>
                  <span className="font-semibold text-gray-900" data-testid="text-upgrade-seam-lock-monthly">
                    ${seamLockMonthly.toFixed(2)}/mo
                  </span>
                </div>
                <div className="flex justify-between text-sm pt-1 border-t border-blue-200">
                  <span className="text-gray-700 font-medium">All-in monthly total</span>
                  <span className="font-bold text-gray-900" data-testid="text-upgrade-all-in-monthly">
                    ${(Math.round(allInMonthlyTotal * 100) / 100).toFixed(2)}/mo
                  </span>
                </div>
                <p className="text-[11px] text-gray-600 pt-1" data-testid="text-upgrade-seam-lock-note">
                  Smart locks stay a monthly metered charge {switchToAnnual ? "even on the annual plan" : ""} — they're billed each month based on the {seamLockCount} non-TTLock lock{seamLockCount !== 1 ? "s" : ""} you have connected{switchToAnnual ? ", and aren't included in today's annual charge" : ""}.
                </p>
              </>
            )}
          </div>
        </div>
        {promoInvalid && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="notice-upgrade-promo-invalid">
            <p className="text-xs text-red-700">
              The promo code you staged can't be applied. Remove or replace it above before continuing to checkout.
            </p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-upgrade-cancel">
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm({ switchToAnnual })}
            disabled={isPending || promoInvalid}
            className="bg-brand hover:bg-brand-hover text-white"
            data-testid="button-upgrade-confirm"
          >
            {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upgrade & Charge"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DowngradeDialog({
  open,
  onOpenChange,
  currentPlan,
  targetPlan,
  periodEnd,
  planFieldErrors,
  noActiveSubscription = false,
  onConfirm,
  isPending,
  promoInvalid = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPlan: AccountType;
  targetPlan: AccountType | null;
  periodEnd: string | null;
  planFieldErrors: Record<string, string>;
  noActiveSubscription?: boolean;
  onConfirm: () => void;
  isPending: boolean;
  promoInvalid?: boolean;
}) {
  const [confirmText, setConfirmText] = useState("");
  const [confirmTextError, setConfirmTextError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) {
      setConfirmText("");
      setConfirmTextError(null);
    }
  }, [open]);

  if (!targetPlan) return null;
  const currentFeatures = PLAN_FEATURES[currentPlan];
  const targetFeatures = PLAN_FEATURES[targetPlan];
  const lostFeatures = currentFeatures
    ? currentFeatures.included.filter((f) => !targetFeatures?.included.includes(f))
    : [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && isPending) return; onOpenChange(o); }}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="dialog-downgrade"
        onEscapeKeyDown={(e) => { if (isPending) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isPending) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (isPending) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowDownCircle className="h-5 w-5 text-amber-600" />
            Downgrade to {targetPlan}
          </DialogTitle>
          <DialogDescription>
            {noActiveSubscription ? (
              <>You'll be sent to checkout to start a new <span className="font-semibold">{targetPlan}</span> subscription. Your card will be charged immediately and your current plan will be replaced.</>
            ) : (
              <>Your downgrade will take effect on <span className="font-semibold">{formatDate(periodEnd)}</span> at the end of your current billing period. You'll keep all current features until then.</>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {(planFieldErrors.plan || planFieldErrors.scheduled) && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="error-downgrade-plan">
              <p className="text-sm text-red-700">{planFieldErrors.plan || planFieldErrors.scheduled}</p>
            </div>
          )}
          {lostFeatures.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm font-semibold text-amber-900 mb-2">You will lose access to:</p>
              <ul className="space-y-1.5">
                {lostFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-amber-900">
                    <X className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="downgrade-confirm" className="text-sm">
              Type <span className="font-mono font-bold">DOWNGRADE</span> to confirm:
            </Label>
            <Input
              id="downgrade-confirm"
              value={confirmText}
              onChange={(e) => {
                setConfirmText(e.target.value);
                if (confirmTextError) setConfirmTextError(null);
              }}
              placeholder="DOWNGRADE"
              className={confirmTextError ? "border-red-500 focus-visible:ring-red-500" : ""}
              data-testid="input-downgrade-confirm"
            />
            {confirmTextError && (
              <p className="text-xs text-red-600 mt-1" data-testid="error-downgrade-confirm">{confirmTextError}</p>
            )}
          </div>
        </div>
        {noActiveSubscription && promoInvalid && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="notice-downgrade-promo-invalid">
            <p className="text-xs text-red-700">
              The promo code you staged can't be applied. Remove or replace it above before continuing to checkout.
            </p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-downgrade-cancel">
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (confirmText !== "DOWNGRADE") {
                setConfirmTextError('Please type "DOWNGRADE" exactly to confirm.');
                return;
              }
              setConfirmTextError(null);
              onConfirm();
            }}
            disabled={isPending || (noActiveSubscription && promoInvalid)}
            variant="destructive"
            data-testid="button-downgrade-confirm"
          >
            {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : (noActiveSubscription ? "Continue to checkout" : "Schedule downgrade")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CycleChangeDialog({
  open,
  onOpenChange,
  currentCycle,
  targetCycle,
  periodEnd,
  monthlyTotal,
  annualTotal,
  seamLockUsage,
  cycleFieldErrors,
  onConfirm,
  isPending,
  formatCurrency: formatCurrencyProp,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentCycle: string;
  targetCycle: "monthly" | "annual" | null;
  periodEnd: string | null;
  monthlyTotal: number;
  annualTotal?: number;
  seamLockUsage?: BillingSummary["seamLockUsage"] | null;
  cycleFieldErrors: Record<string, string>;
  onConfirm: () => void;
  isPending: boolean;
  formatCurrency?: (amount: number) => string;
}) {
  const isUpgrade = targetCycle === "annual";
  // Task #1835 — Fetch the exact Stripe-calculated prorated charge so the
  // dialog shows a live amount rather than the legacy 0.833 estimate.
  const previewQuery = useQuery<{ chargedAmountCents: number | null; renewalDate: string | null }>({
    queryKey: ["/api/app/billing/preview-cycle-change", targetCycle],
    queryFn: async () => {
      const res = await apiRequest("POST", "/api/app/billing/preview-cycle-change", { cycle: targetCycle });
      return res.json();
    },
    enabled: open && isUpgrade && !!targetCycle,
    staleTime: 30_000,
  });
  if (!targetCycle) return null;
  const estimatedAnnualTotal = Math.round(monthlyTotal * 12 * 0.833);
  const fmt = formatCurrencyProp ?? ((n: number) => `$${Math.round(n)}`);
  const seamLockMonthly = (seamLockUsage?.totalCents ?? 0) / 100;
  const seamLockCount = seamLockUsage?.seamLockCount ?? 0;
  const hasSeamLocks = seamLockCount > 0;
  const newPlanMonthlyEquivalent = isUpgrade
    ? (typeof annualTotal === "number" ? annualTotal / 12 : monthlyTotal * 0.833)
    : monthlyTotal;
  const allInMonthlyTotal = newPlanMonthlyEquivalent + seamLockMonthly;
  const previewCents = previewQuery.data?.chargedAmountCents;
  const hasPreview = typeof previewCents === "number";
  const previewLoading = isUpgrade && previewQuery.isLoading;
  const chargeTodayLabel = hasPreview
    ? fmt(previewCents! / 100)
    : `~${fmt(estimatedAnnualTotal)}`;
  const renewalDateLabel = previewQuery.data?.renewalDate
    ? format(new Date(previewQuery.data.renewalDate), "MMM d, yyyy")
    : `~${format(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), "MMM d, yyyy")}`;
  const renewalIsExact = Boolean(previewQuery.data?.renewalDate);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && isPending) return; onOpenChange(o); }}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="dialog-cycle-change"
        onEscapeKeyDown={(e) => { if (isPending) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isPending) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (isPending) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isUpgrade ? (
              <ArrowUpCircle className="h-5 w-5 text-brand" />
            ) : (
              <Clock className="h-5 w-5 text-amber-600" />
            )}
            Switch to {targetCycle} billing
          </DialogTitle>
          <DialogDescription>
            {isUpgrade
              ? hasPreview
                ? `Annual billing saves you ~17%. Your subscription will switch immediately and you'll be charged ${chargeTodayLabel} today (the prorated annual amount, with credit for unused monthly time).`
                : previewLoading
                  ? "Annual billing saves you ~17%. Calculating your exact charge for today…"
                  : "Annual billing saves you ~17%. Your subscription will switch immediately and you'll be charged for the year today (with prorated credit for unused monthly time)."
              : `You're paid through ${formatDate(periodEnd)}. You'll stay on annual until then, and switch to monthly billing on that date.`}
          </DialogDescription>
        </DialogHeader>
        {cycleFieldErrors.cycle && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 my-2" data-testid="error-cycle-change">
            <p className="text-sm text-red-700">{cycleFieldErrors.cycle}</p>
          </div>
        )}
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 my-2 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Current</span>
            <span className="font-semibold text-gray-900">{currentCycle}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">New cycle</span>
            <span className="font-semibold text-gray-900">{targetCycle}</span>
          </div>
          {isUpgrade ? (
            <>
              <div className="flex justify-between text-sm pt-1 border-t border-blue-200">
                <span className="text-gray-700 font-medium">
                  {hasPreview ? "Charge today" : "Est. charge today"}
                </span>
                <span className="font-bold text-brand" data-testid="text-cycle-charge-today">
                  {previewLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin inline" />
                  ) : (
                    chargeTodayLabel
                  )}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  {renewalIsExact ? "Annual renewal" : "Est. annual renewal"}
                </span>
                <span className="font-semibold text-gray-900">
                  {renewalIsExact ? renewalDateLabel : renewalDateLabel}
                </span>
              </div>
              {hasSeamLocks && (
                <>
                  <div className="flex justify-between text-sm pt-1 border-t border-blue-200">
                    <span className="text-gray-600">Plan (monthly equivalent)</span>
                    <span className="font-semibold text-gray-900" data-testid="text-cycle-plan-monthly-equivalent">
                      {fmt(newPlanMonthlyEquivalent)}/mo
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      Smart lock add-on (billed monthly)
                    </span>
                    <span className="font-semibold text-gray-900" data-testid="text-cycle-seam-lock-monthly">
                      {fmt(seamLockMonthly)}/mo
                    </span>
                  </div>
                  <div className="flex justify-between text-sm pt-1 border-t border-blue-200">
                    <span className="text-gray-700 font-medium">All-in monthly total</span>
                    <span className="font-bold text-gray-900" data-testid="text-cycle-all-in-monthly">
                      {fmt(allInMonthlyTotal)}/mo
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-600 pt-1" data-testid="text-cycle-seam-lock-note">
                    Smart locks stay a monthly metered charge even on the annual plan — they're billed each month based on the {seamLockCount} non-TTLock lock{seamLockCount !== 1 ? "s" : ""} you have connected, and aren't included in today's annual charge.
                  </p>
                </>
              )}
            </>
          ) : (
            <div className="flex justify-between text-sm pt-1 border-t border-blue-200">
              <span className="text-gray-700 font-medium">Charge today</span>
              <span className="font-semibold text-green-600">None</span>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-cycle-cancel">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isPending}
            className={isUpgrade ? "bg-brand hover:bg-brand-hover text-white" : ""}
            variant={isUpgrade ? "default" : "secondary"}
            data-testid="button-cycle-confirm"
          >
            {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : isUpgrade ? "Switch & Charge" : "Schedule switch"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CancelDialog({
  open,
  onOpenChange,
  errorMessage,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  errorMessage: string | null;
  onConfirm: () => void;
  isPending: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && isPending) return; onOpenChange(o); }}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="dialog-cancel"
        onEscapeKeyDown={(e) => { if (isPending) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isPending) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (isPending) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>Cancel subscription?</DialogTitle>
        </DialogHeader>
        <div className="py-2 space-y-3">
          {errorMessage && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="error-cancel">
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}
          <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">
              Canceling will downgrade your account at the end of the current billing period. You'll lose access to paid features.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-cancel-dialog-dismiss">Keep subscription</Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending}
            data-testid="button-confirm-cancel"
          >
            {isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Yes, cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

